import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
  addDays,
  format,
  isAfter,
  isBefore,
  isValid,
  startOfDay,
} from 'date-fns' // eslint-disable-line import/no-duplicates
import { ja } from 'date-fns/locale' // eslint-disable-line import/no-duplicates
import { useRef, useState } from 'react'
import { maxSelectableDays } from 'src/features/scheduledReportSummary/ScheduledReportSummary'

type Props = {
  onChange: (range: [string, string] | null) => void
  value: [string, string] | null
}

export const LimitedRangePicker = ({ onChange, value }: Props) => {
  const [start, end] = value ?? [null, null]

  const [startDate, setStartDate] = useState<Date | null>(
    start ? new Date(start) : null,
  )
  const [endDate, setEndDate] = useState<Date | null>(
    end ? new Date(end) : null,
  )

  // onClose/onBlur 時に state の非同期更新に依存しないよう最新値を同期追跡
  const startDateRef = useRef<Date | null>(start ? new Date(start) : null)
  const endDateRef = useRef<Date | null>(end ? new Date(end) : null)
  // カレンダーが閉じた直後の onBlur 重複通知を防ぐフラグ
  const justClosedPickerRef = useRef(false)

  const shouldDisableStartDate = (date: Date) => {
    if (isAfter(date, new Date())) return true
    return false
  }

  const shouldDisableEndDate = (date: Date) => {
    if (isAfter(date, new Date())) return true
    if (
      startDate &&
      isValid(startDate) &&
      isBefore(date, startOfDay(startDate))
    )
      return true
    if (
      startDate &&
      isValid(startDate) &&
      isAfter(date, addDays(startDate, maxSelectableDays - 1))
    )
      return true
    return false
  }

  const calcEndDate = (date: Date) => {
    const today = new Date()
    const maxEnd = addDays(date, maxSelectableDays - 1)
    return isAfter(maxEnd, today) ? today : maxEnd
  }

  const notifyChange = () => {
    const s = startDateRef.current
    const e = endDateRef.current
    if (s && isValid(s) && e && isValid(e)) {
      onChange([format(s, 'yyyy-MM-dd'), format(e, 'yyyy-MM-dd')])
    }
  }

  const handleStartChange = (date: Date | null) => {
    if (date !== null && !isValid(date)) {
      setStartDate(date)
      return
    }
    if (!date) {
      startDateRef.current = null
      endDateRef.current = null
      setStartDate(null)
      setEndDate(null)
      onChange(null)
      return
    }
    const newEndDate = calcEndDate(date)
    startDateRef.current = date
    endDateRef.current = newEndDate
    setStartDate(date)
    setEndDate(newEndDate)
  }

  const handleEndChange = (date: Date | null) => {
    if (date !== null && !isValid(date)) {
      setEndDate(date)
      return
    }
    if (!date) {
      startDateRef.current = null
      endDateRef.current = null
      setStartDate(null)
      setEndDate(null)
      onChange(null)
      return
    }
    endDateRef.current = date
    setEndDate(date)
  }

  const handlePickerClose = () => {
    justClosedPickerRef.current = true
    notifyChange()
  }

  const handleTextFieldBlur = () => {
    if (justClosedPickerRef.current) {
      justClosedPickerRef.current = false
      return
    }
    notifyChange()
  }

  const handleReset = () => {
    startDateRef.current = null
    endDateRef.current = null
    setStartDate(null)
    setEndDate(null)
    onChange(null)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Stack alignItems="center" direction="row" spacing={1}>
        <Box data-testid="limited-range-picker-start">
          <DatePicker
            format="yyyy-MM-dd"
            onClose={handlePickerClose}
            onChange={handleStartChange}
            shouldDisableDate={shouldDisableStartDate}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 164 },
                slotProps: { htmlInput: { placeholder: '開始日' } },
                onBlur: handleTextFieldBlur,
              },
              toolbar: { hidden: true },
            }}
            value={startDate}
          />
        </Box>
        <Typography>〜</Typography>
        <Box data-testid="limited-range-picker-end">
          <DatePicker
            format="yyyy-MM-dd"
            onClose={handlePickerClose}
            onChange={handleEndChange}
            shouldDisableDate={shouldDisableEndDate}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 164 },
                slotProps: { htmlInput: { placeholder: '終了日' } },
                onBlur: handleTextFieldBlur,
              },
              toolbar: { hidden: true },
            }}
            value={endDate}
          />
        </Box>
        {(startDate !== null || endDate !== null) && (
          <Tooltip title="日付をリセット">
            <IconButton
              aria-label="日付をリセット"
              onClick={handleReset}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </LocalizationProvider>
  )
}
